package dev.lingvo.site.components.editor

import kotlin.math.abs
import kotlin.math.floor

private typealias EditorEffectFactory = (Long) -> EditorViewEffect

private fun EditorWorkspaceState.transition(
    nextState: EditorWorkspaceState = this,
    effectFactories: List<EditorEffectFactory> = emptyList(),
): EditorTransition {
    var nextId = nextState.nextEffectId
    val effects = effectFactories.map { factory -> factory(nextId++) }
    return EditorTransition(nextState.copy(nextEffectId = nextId), effects)
}
internal fun EditorWorkspaceState.asTransition(): EditorTransition = transition()

private fun focusFactory(focus: EditorDeferredFocus): EditorEffectFactory = { id ->
    EditorViewEffect.Focus(id, focus.target, focus.fallback)
}

private fun restoreFactory(topPx: Double): EditorEffectFactory = { id ->
    EditorViewEffect.RestoreQueueScroll(id, topPx)
}

internal fun EditorWorkspaceState.restartMeasurement(
    deferredFocus: EditorDeferredFocus?,
    width: Int = viewportWidth,
): EditorWorkspaceState = copy(
    viewportWidth = width,
    viewport = EditorViewportClass.WORKBENCH,
    measuredLayoutDisposition = EditorMeasuredLayoutDisposition.MEASURING,
    layoutMeasurementId = layoutMeasurementId + 1,
    measuredScrollbarGutterPx = null,
    editorSidePaddingPx = EDITOR_PREFERRED_SIDE_PADDING_PX,
    focusAfterMeasurement = deferredFocus,
)

internal fun EditorWorkspaceState.emitOrDeferFocus(
    deferredFocus: EditorDeferredFocus?,
    restoreQueue: Boolean,
): EditorTransition {
    var state = copy(focusAfterQueueRows = null)
    if (state.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING) {
        return state.copy(focusAfterMeasurement = deferredFocus).transition()
    }

    state = state.copy(focusAfterMeasurement = null)
    val queueMounted = restoreQueue && state.mountsQueueList()
    val waitForRows = queueMounted && state.pendingQueueScrollResetRevision != null
    if (waitForRows && deferredFocus != null) {
        state = state.copy(focusAfterQueueRows = deferredFocus)
    }
    val factories = buildList {
        if (queueMounted && !waitForRows) add(restoreFactory(state.queueScrollTopPx))
        if (!waitForRows && deferredFocus != null) add(focusFactory(deferredFocus))
    }
    return state.transition(effectFactories = factories)
}

private fun EditorWorkspaceState.mountsQueueListFor(
    disposition: EditorMeasuredLayoutDisposition,
): Boolean = when (disposition) {
    EditorMeasuredLayoutDisposition.MEASURING,
    EditorMeasuredLayoutDisposition.WORKBENCH,
    -> utility == EditorUtilitySurface.NONE && queueMode == EditorQueueMode.LIST

    EditorMeasuredLayoutDisposition.NOT_APPLICABLE,
    EditorMeasuredLayoutDisposition.COMPACT_FALLBACK,
    -> primaryView == EditorPrimaryView.QUEUE &&
        utility == EditorUtilitySurface.NONE &&
        queueMode == EditorQueueMode.LIST
}

/**
 * Leave [EditorMeasuredLayoutDisposition.MEASURING] whatever happened, using whatever the DOM can
 * still tell us.
 *
 * **A measurement probe hides the workspace, so a probe that never resolves is a blank editor.**
 * That is not hypothetical: opening the Assist pane restarted measurement, the accept path silently
 * declined the result, and the workspace stayed `visibility: hidden` — top bar and nothing else —
 * with no error, no failed request and no way back except a reload. It reproduced on every locale
 * at every desktop width.
 *
 * The specific accept bug is fixed at its own call site. This exists so that the *class* of bug
 * cannot present that way again: whatever declines a measurement in future, the worst outcome is a
 * layout chosen from stale numbers, never an invisible one. A wrong writing width is a design
 * defect; an empty screen is a broken product.
 *
 * [contentClientWidth] is null when even the fallback read failed, in which case there is nothing
 * to choose a workbench layout from and the compact one — which needs no measurement — is correct.
 */
internal fun EditorWorkspaceState.abandonMeasurement(
    contentClientWidth: Double?,
    gutterPx: Double,
): EditorTransition {
    if (measuredLayoutDisposition != EditorMeasuredLayoutDisposition.MEASURING) {
        return EditorTransition(this)
    }
    val contentInnerWidth = contentClientWidth?.minus(editorSidePaddingPx * 2.0)
    val disposition = if (contentInnerWidth != null && contentInnerWidth >= EDITOR_MIN_WRITING_WIDTH_PX) {
        EditorMeasuredLayoutDisposition.WORKBENCH
    } else {
        EditorMeasuredLayoutDisposition.COMPACT_FALLBACK
    }
    return settleMeasuredLayout(disposition, gutterPx)
}

private fun EditorWorkspaceState.settleMeasuredLayout(
    disposition: EditorMeasuredLayoutDisposition,
    gutterPx: Double,
): EditorTransition {
    val compactUtility = disposition == EditorMeasuredLayoutDisposition.COMPACT_FALLBACK &&
        utility != EditorUtilitySurface.NONE
    val utilityPhase = utilityPresencePhase()
    val compactUtilityPending = compactUtility &&
        utilityPhase == EditorUtilityPresencePhase.PENDING_ENTER
    val compactUtilityExiting = compactUtility &&
        utilityPhase == EditorUtilityPresencePhase.EXITING
    val deferredFocus = when {
        compactUtilityPending || compactUtilityExiting -> null
        compactUtility -> utilityOpenFocus ?: EditorDeferredFocus(
            target = EditorFocusRef.ElementId(EDITOR_UTILITY_CLOSE_ID),
            fallback = focusAfterMeasurement?.target,
        )
        else -> focusAfterMeasurement
    }
    var state = copy(
        measuredLayoutDisposition = disposition,
        measuredScrollbarGutterPx = gutterPx.coerceAtLeast(0.0),
        focusAfterMeasurement = null,
        utilityOpenFocus = utilityOpenFocus.takeIf { compactUtilityPending },
        focusAfterQueueRows = focusAfterQueueRows.takeIf {
            mountsQueueListFor(disposition)
        },
    )
    val queueMounted = state.mountsQueueList()
    val waitForRows = queueMounted && state.pendingQueueScrollResetRevision != null
    if (waitForRows && deferredFocus != null) {
        state = state.copy(focusAfterQueueRows = deferredFocus)
    }
    val factories = buildList {
        if (queueMounted && !waitForRows) add(restoreFactory(state.queueScrollTopPx))
        if (!waitForRows && deferredFocus != null) add(focusFactory(deferredFocus))
    }
    return state.transition(effectFactories = factories)
}

internal fun EditorWorkspaceState.withViewportWidth(
    width: Int,
    focusedDescendant: EditorFocusRef?,
): EditorTransition {
    if (width == viewportWidth) return EditorTransition(this)

    val descendantFocus = focusedDescendant?.let { EditorDeferredFocus(it, null) }
    if (width >= EDITOR_WORKBENCH_THRESHOLD_PX) {
        return restartMeasurement(
            deferredFocus = descendantFocus ?: focusAfterMeasurement,
            width = width,
        ).transition()
    }

    val deferredFocus = descendantFocus ?: focusAfterMeasurement
    val nextState = copy(
        viewportWidth = width,
        viewport = EditorViewportClass.COMPACT,
        measuredLayoutDisposition = EditorMeasuredLayoutDisposition.NOT_APPLICABLE,
        measuredScrollbarGutterPx = null,
        editorSidePaddingPx = EDITOR_PREFERRED_SIDE_PADDING_PX,
        focusAfterQueueRows = focusAfterQueueRows.takeIf {
            mountsQueueListFor(EditorMeasuredLayoutDisposition.NOT_APPLICABLE)
        },
    )
    val restoreQueue = nextState.mountsQueueList() &&
        (viewport == EditorViewportClass.WORKBENCH ||
            measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING ||
            !mountsQueueList())
    return nextState.emitOrDeferFocus(deferredFocus, restoreQueue)
}

internal fun EditorWorkspaceState.invalidateWorkbenchMeasurement(): EditorTransition {
    if (viewport != EditorViewportClass.WORKBENCH) return EditorTransition(this)
    return restartMeasurement(focusAfterMeasurement).transition()
}

internal fun EditorWorkspaceState.applyWorkbenchMeasurement(
    measurementId: Long,
    contentClientWidth: Double,
    gutterPx: Double,
    activeTargetWidth: Double?,
): EditorTransition {
    if (
        measuredLayoutDisposition != EditorMeasuredLayoutDisposition.MEASURING ||
        measurementId != layoutMeasurementId
    ) {
        return EditorTransition(this)
    }

    val contentInnerWidth = contentClientWidth - editorSidePaddingPx * 2.0
    val targetPasses = activeTargetWidth == null ||
        (activeTargetWidth >= EDITOR_MIN_WRITING_WIDTH_PX &&
            abs(activeTargetWidth - contentInnerWidth) <= 2.0)
    if (contentInnerWidth >= EDITOR_MIN_WRITING_WIDTH_PX && targetPasses) {
        return settleMeasuredLayout(EditorMeasuredLayoutDisposition.WORKBENCH, gutterPx)
    }

    if (editorSidePaddingPx == EDITOR_PREFERRED_SIDE_PADDING_PX) {
        val adaptive = floor(
            (contentClientWidth - EDITOR_MIN_WRITING_WIDTH_PX) / 2.0,
        ).toInt()
        if (adaptive >= EDITOR_MIN_SIDE_PADDING_PX) {
            return copy(
                editorSidePaddingPx = minOf(31, adaptive),
                measuredScrollbarGutterPx = gutterPx.coerceAtLeast(0.0),
            ).transition()
        }
    }
    return settleMeasuredLayout(EditorMeasuredLayoutDisposition.COMPACT_FALLBACK, gutterPx)
}

internal fun EditorWorkspaceState.recordQueueScroll(topPx: Double): EditorWorkspaceState = copy(
    queueScrollTopPx = topPx.coerceAtLeast(0.0),
)

internal fun EditorWorkspaceState.onQueueQueryChanged(revision: Long): EditorTransition {
    if (revision <= queueQueryRevision) return EditorTransition(this)
    return copy(
        queueQueryRevision = revision,
        pendingQueueScrollResetRevision = revision,
        queueScrollTopPx = 0.0,
        focusAfterQueueRows = null,
    ).transition()
}

internal fun EditorWorkspaceState.onQueueRowsApplied(revision: Long): EditorTransition {
    if (pendingQueueScrollResetRevision != revision || !mountsQueueList()) {
        return EditorTransition(this)
    }
    val deferredFocus = focusAfterQueueRows
    if (measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING) {
        return copy(
            pendingQueueScrollResetRevision = null,
            focusAfterQueueRows = null,
            focusAfterMeasurement = deferredFocus ?: focusAfterMeasurement,
        ).transition()
    }
    return copy(
        pendingQueueScrollResetRevision = null,
        focusAfterQueueRows = null,
    ).transition(
        effectFactories = buildList {
            add(restoreFactory(0.0))
            if (deferredFocus != null) add(focusFactory(deferredFocus))
        },
    )
}

internal fun EditorWorkspaceState.requestFocus(
    target: EditorFocusRef,
    fallback: EditorFocusRef? = null,
): EditorTransition {
    val deferredFocus = EditorDeferredFocus(target, fallback)
    val state = copy(focusAfterQueueRows = null)
    if (state.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING) {
        return state.copy(focusAfterMeasurement = deferredFocus).transition()
    }
    return state.transition(effectFactories = listOf(focusFactory(deferredFocus)))
}

internal fun EditorWorkspaceState.showQueue(focus: EditorFocusRef? = null): EditorTransition {
    val deferredFocus = focus?.let { EditorDeferredFocus(it, EditorFocusRef.Search) }
    val nextState = copy(
        primaryView = if (renderedLayout() == EditorRenderedLayout.WORKBENCH) {
            EditorPrimaryView.EDITOR
        } else {
            EditorPrimaryView.QUEUE
        },
        queueMode = EditorQueueMode.LIST,
        utility = EditorUtilitySurface.NONE,
        utilityReturnFocus = null,
        utilityPresence = EditorUtilityPresenceState.closed(),
        utilityOpenFocus = null,
        utilityCloseFocus = null,
        filtersReturnFocus = null,
        focusAfterQueueRows = null,
    )
    if (viewport == EditorViewportClass.WORKBENCH && utility != EditorUtilitySurface.NONE) {
        return nextState.restartMeasurement(deferredFocus).transition()
    }
    val restoreQueue = nextState.mountsQueueList() &&
        (measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING || !mountsQueueList())
    return nextState.emitOrDeferFocus(deferredFocus, restoreQueue)
}

internal fun EditorWorkspaceState.showEditor(focus: EditorFocusRef? = null): EditorTransition {
    val nextState = copy(
        primaryView = EditorPrimaryView.EDITOR,
        focusAfterQueueRows = null,
    )
    return nextState.emitOrDeferFocus(
        deferredFocus = focus?.let { EditorDeferredFocus(it, EditorFocusRef.Search) },
        restoreQueue = false,
    )
}

internal fun EditorWorkspaceState.openFilters(invoker: EditorFocusRef): EditorTransition {
    val nextState = copy(
        primaryView = if (renderedLayout() == EditorRenderedLayout.WORKBENCH) {
            EditorPrimaryView.EDITOR
        } else {
            EditorPrimaryView.QUEUE
        },
        queueMode = EditorQueueMode.FILTERS,
        utility = EditorUtilitySurface.NONE,
        utilityReturnFocus = null,
        utilityPresence = EditorUtilityPresenceState.closed(),
        utilityOpenFocus = null,
        utilityCloseFocus = null,
        filtersReturnFocus = invoker,
        overflowOpen = false,
        overflowReturnFocus = null,
        focusAfterQueueRows = null,
        focusAfterMeasurement = null,
    )
    if (viewport == EditorViewportClass.WORKBENCH && utility != EditorUtilitySurface.NONE) {
        return nextState.restartMeasurement(null).transition()
    }
    return nextState.transition()
}

internal fun EditorWorkspaceState.closeFilters(): EditorTransition {
    val deferredFocus = filtersReturnFocus?.let { EditorDeferredFocus(it, EditorFocusRef.Search) }
    val nextState = copy(
        primaryView = if (renderedLayout() == EditorRenderedLayout.WORKBENCH) {
            EditorPrimaryView.EDITOR
        } else {
            EditorPrimaryView.QUEUE
        },
        queueMode = EditorQueueMode.LIST,
        filtersReturnFocus = null,
        focusAfterQueueRows = null,
    )
    return nextState.emitOrDeferFocus(
        deferredFocus,
        restoreQueue = nextState.mountsQueueList() && !mountsQueueList(),
    )
}

internal fun EditorWorkspaceState.enterConflict(target: EditorFocusRef): EditorTransition {
    val queueWasMounted = mountsQueueList()
    val deferredFocus = EditorDeferredFocus(target, null)
    val nextState = copy(
        primaryView = EditorPrimaryView.EDITOR,
        queueMode = EditorQueueMode.LIST,
        utility = EditorUtilitySurface.NONE,
        utilityReturnFocus = null,
        utilityPresence = EditorUtilityPresenceState.closed(),
        utilityOpenFocus = null,
        utilityCloseFocus = null,
        filtersReturnFocus = null,
        overflowOpen = false,
        overflowReturnFocus = null,
        modal = EditorModalSurface.NONE,
        modalReturnFocus = null,
        focusAfterQueueRows = null,
    )
    if (viewport == EditorViewportClass.WORKBENCH && utility != EditorUtilitySurface.NONE) {
        return nextState.restartMeasurement(deferredFocus).transition()
    }
    return nextState.emitOrDeferFocus(
        deferredFocus,
        restoreQueue = nextState.mountsQueueList() && !queueWasMounted,
    )
}
